'use client';

import { useEffect } from 'react';
import { useTranslations } from 'next-intl';
import { Link, useRouter } from '@/i18n/routing';
import { useAuth } from '@/contexts/auth-context';
import { useFirstAccessibleRoute } from '@/hooks/use-nav';
import { Button } from '@/components/ui/button';
import {
  isSuperAdmin,
  hasAnyRole,
  hasAnyPermission
} from '../utils/permissions';
import {
  capturePendingRedirect,
  REDIRECT_QUERY_PARAM
} from '../utils/redirect';
import { Loader2, ShieldX } from 'lucide-react';

interface AuthGuardProps {
  children: React.ReactNode;
  allowedRoles?: string[];
  requiredPermissions?: string[];
  fallback?: React.ReactNode;
}

export function AuthGuard({
  children,
  allowedRoles,
  requiredPermissions,
  fallback
}: AuthGuardProps) {
  const { status, user } = useAuth();
  const router = useRouter();
  const firstRoute = useFirstAccessibleRoute();
  const t = useTranslations('auth.guard');

  useEffect(() => {
    if (status === 'unauthenticated') {
      // Safe to re-run: sanitizeRedirectPath rejects /login*, so we never
      // capture our own redirect target.
      const target = capturePendingRedirect();
      if (target) {
        router.replace(
          `/login?${REDIRECT_QUERY_PARAM}=${encodeURIComponent(target)}`
        );
      } else {
        router.replace('/login');
      }
    }
  }, [status, router]);

  if (status === 'loading') {
    return (
      fallback ?? (
        <div className='flex min-h-screen items-center justify-center'>
          <Loader2 className='h-8 w-8 animate-spin' />
        </div>
      )
    );
  }

  if (status === 'unauthenticated' || status === 'otp_pending') {
    return null;
  }

  if (user && !isSuperAdmin(user)) {
    const hasRoleAccess = allowedRoles ? hasAnyRole(user, allowedRoles) : true;
    const hasPermissionAccess = requiredPermissions
      ? hasAnyPermission(user, requiredPermissions)
      : true;

    if (!hasRoleAccess || !hasPermissionAccess) {
      // Always offer a way out. Whatever sent the user here — a stale link, a
      // role change mid-session, an affordance that slipped past its gate —
      // this screen is otherwise a dead end with only the sidebar to escape it.
      return (
        <div className='flex min-h-[60vh] flex-col items-center justify-center gap-4 text-center'>
          <ShieldX className='text-destructive h-16 w-16' />
          <h2 className='text-2xl font-semibold'>{t('accessDenied')}</h2>
          <p className='text-muted-foreground max-w-md'>{t('noPermission')}</p>
          <Button asChild variant='outline'>
            <Link href={firstRoute}>{t('goToAccessiblePage')}</Link>
          </Button>
        </div>
      );
    }
  }

  return <>{children}</>;
}
